import { DRUM_INSTRUMENTS, MAX_CLIP_LOOPS, MAX_FADE_SECONDS, MAX_SONG_BARS } from '../domain/schemas';
import type { Command, ParseError } from './commands';
import { SECTION_WORDS, parseInstrument } from './musicGrammar';

/**
 * Deterministic grammar for sample and clip prompts (Phase 2). Every command acts on an
 * explicit UI selection ("the selected clip", "this sample"); the executor fails with a
 * clear message when nothing is selected. Unrecognised wording is not guessed at.
 */
type Result = { command: Command } | { error: ParseError } | null;

const err = (segment: string, message: string, example: string): Result => ({
  error: { kind: 'invalid-value', message, segment, suggestions: [example] },
});

const CLIP = String.raw`(?:the |this |that |my )?(?:selected |current )?(?:audio )?clip`;
const SAMPLE = String.raw`(?:the |this |that |my )?(?:selected |current )?(?:sample|audio file|recording|sound)`;

/** "2 seconds", "2s", "500 ms", "1.5 sec" → seconds. */
function parseDuration(amount: string, unit: string | undefined): number | undefined {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return unit && /^(ms|milliseconds?)$/.test(unit) ? n / 1000 : n;
}
const DURATION = String.raw`([\d.]+) ?(ms|milliseconds?|s|secs?|seconds?)?`;

export function parseAudioSegment(segment: string): Result {
  let m: RegExpMatchArray | null;

  // ---- Sample instruments
  if ((m = segment.match(new RegExp(`^(?:add|assign|put|load|use|set) ${SAMPLE} (?:to|on|onto|as|for|in) (?:the )?(.+?)(?: track| sound| instrument)?$`)))) {
    const instrument = parseInstrument(m[1]!);
    if (!instrument) return err(segment, `Unknown track "${m[1]}". Samples go on kick, snare, clap, hats or perc.`, 'add the selected sample to the kick track');
    if (!DRUM_INSTRUMENTS.includes(instrument)) {
      return err(segment, 'Samples can only be assigned to drum and percussion tracks (kick, snare, clap, hats, perc).', 'add the selected sample to the snare track');
    }
    return { command: { type: 'sample.assign', instrument } };
  }
  if (
    (m = segment.match(/^(?:remove|unassign|clear|drop) (?:the )?sample (?:from|on) (?:the )?(.+?)(?: track)?$/)) ||
    (m = segment.match(/^(?:use|restore) (?:the )?(?:built-in|built in|default|synth|original) (.+?)(?: sound| track)?$/))
  ) {
    const instrument = parseInstrument(m[1]!);
    if (instrument && DRUM_INSTRUMENTS.includes(instrument)) return { command: { type: 'sample.unassign', instrument } };
    return err(segment, `Unknown drum track "${m[1]}".`, 'remove the sample from the kick');
  }

  // ---- Placing a sample as a clip
  if ((m = segment.match(new RegExp(`^(?:place|put|add|drop|insert) ${SAMPLE}(?: on the timeline)? (?:at|on|to) (?:the )?(.+)$`)))) {
    const where = m[1]!.trim();
    if (/^(start|beginning) (?:of )?(?:the )?(song|arrangement|track|timeline)$|^(start|beginning)$/.test(where)) {
      return { command: { type: 'sample.place', at: { kind: 'song-start' } } };
    }
    let w: RegExpMatchArray | null;
    if ((w = where.match(/^(?:start|beginning) of (?:the )?(.+)$/))) {
      const section = SECTION_WORDS[w[1]!.trim()];
      if (section) return { command: { type: 'sample.place', at: { kind: 'section', section } } };
      return err(segment, `Unknown section "${w[1]}". Use intro, verse, buildup, drop, breakdown or outro.`, 'place the selected sample at the start of the drop');
    }
    if ((w = where.match(/^bar (\d+)$/))) {
      const bar = Number(w[1]);
      if (bar < 1 || bar > MAX_SONG_BARS) return err(segment, `Bar must be between 1 and ${MAX_SONG_BARS}.`, 'place the selected sample at bar 9');
      return { command: { type: 'sample.place', at: { kind: 'bar', bar } } };
    }
    const section = SECTION_WORDS[where];
    if (section) return { command: { type: 'sample.place', at: { kind: 'section', section } } };
    return err(segment, `I can place samples at the start of the song, the start of a section, or a bar number.`, 'place the selected sample at the start of the drop');
  }

  // ---- Clip edits
  if (new RegExp(`^reverse ${CLIP}$`).test(segment)) return { command: { type: 'clip.reverse', reverse: true } };
  if (new RegExp(`^(?:un-?reverse ${CLIP}|play ${CLIP} forwards?)$`).test(segment)) return { command: { type: 'clip.reverse', reverse: false } };

  if (
    (m = segment.match(new RegExp(`^fade (in|out) ${CLIP}(?: over| for| by)? ${DURATION}$`))) ||
    (m = segment.match(new RegExp(`^fade ${CLIP} (in|out)(?: over| for| by)? ${DURATION}$`)))
  ) {
    const seconds = parseDuration(m[2]!, m[3]);
    if (seconds === undefined || seconds > MAX_FADE_SECONDS) {
      return err(segment, `Fade length must be between 0 and ${MAX_FADE_SECONDS} seconds.`, 'fade in the selected clip over 2 seconds');
    }
    return { command: { type: 'clip.fade', edge: m[1] as 'in' | 'out', seconds } };
  }
  if ((m = segment.match(new RegExp(`^(?:add )?(?:an? )?${DURATION} fade[- ]?(in|out) (?:to|on) ${CLIP}$`)))) {
    const seconds = parseDuration(m[1]!, m[2]);
    if (seconds === undefined || seconds > MAX_FADE_SECONDS) {
      return err(segment, `Fade length must be between 0 and ${MAX_FADE_SECONDS} seconds.`, 'fade out the selected clip over 1 second');
    }
    return { command: { type: 'clip.fade', edge: m[3] as 'in' | 'out', seconds } };
  }
  if ((m = segment.match(new RegExp(`^(?:remove|clear) (?:the )?fade[- ]?(in|out) (?:from|on) ${CLIP}$`)))) {
    return { command: { type: 'clip.fade', edge: m[1] as 'in' | 'out', seconds: 0 } };
  }

  if (
    (m = segment.match(new RegExp(`^(lower|reduce|decrease|raise|increase|boost|turn down|turn up) ${CLIP}(?: volume| gain| level)?(?: by ([\\d.]+) ?db)?$`))) ||
    (m = segment.match(new RegExp(`^(lower|reduce|decrease|raise|increase|boost) (?:the )?(?:volume|gain|level) of ${CLIP}(?: by ([\\d.]+) ?db)?$`))) ||
    (m = segment.match(new RegExp(`^turn ${CLIP} (down|up)(?: by ([\\d.]+) ?db)?$`)))
  ) {
    const amount = m[2] === undefined ? 3 : Number(m[2]);
    if (!Number.isFinite(amount) || amount <= 0 || amount > 48) {
      return err(segment, 'Volume changes must be between 0 and 48 dB.', 'lower the selected clip volume by 6 db');
    }
    const down = /lower|reduce|decrease|down/.test(m[1]!);
    return { command: { type: 'clip.adjustGain', deltaDb: down ? -amount : amount } };
  }
  if ((m = segment.match(new RegExp(`^set ${CLIP}(?: volume| gain| level) to (-?[\\d.]+) ?db$`)))) {
    const db = Number(m[1]);
    if (!Number.isFinite(db) || db < -48 || db > 12) return err(segment, 'Clip volume must be between -48 and +12 dB.', 'set the selected clip volume to -3 db');
    return { command: { type: 'clip.setGain', db } };
  }

  if (new RegExp(`^(?:duplicate|copy|clone) ${CLIP}$`).test(segment)) return { command: { type: 'clip.duplicate' } };
  if ((m = segment.match(new RegExp(`^(mute|unmute|silence) ${CLIP}$`)))) return { command: { type: 'clip.mute', muted: m[1] !== 'unmute' } };
  if (new RegExp(`^(?:delete|remove) ${CLIP}$`).test(segment)) return { command: { type: 'clip.delete' } };
  if ((m = segment.match(new RegExp(`^(normali[sz]e|don'?t normali[sz]e|un-?normali[sz]e) ${CLIP}$`)))) {
    return { command: { type: 'clip.normalize', normalize: /^normali/.test(m[1]!) } };
  }

  if (
    (m = segment.match(new RegExp(`^set ${CLIP} (?:speed|playback rate|rate) to ([\\d.]+) ?(?:x|×|times)?$`))) ||
    (m = segment.match(new RegExp(`^play ${CLIP} at ([\\d.]+) ?(?:x|×) ?(?:speed)?$`)))
  ) {
    const rate = Number(m[1]);
    if (!Number.isFinite(rate) || rate < 0.25 || rate > 4) {
      return err(segment, 'Playback speed must be between 0.25× and 4× (speed and pitch change together).', 'set the selected clip speed to 1.5x');
    }
    return { command: { type: 'clip.setRate', rate } };
  }
  if (new RegExp(`^(?:time-?stretch|stretch|pitch shift|pitch-shift|transpose) ${CLIP}`).test(segment)) {
    return err(
      segment,
      'Time-stretching and independent pitch shifting are not supported. Changing the speed changes pitch too: try "set the selected clip speed to 1.5x".',
      'set the selected clip speed to 1.5x',
    );
  }

  if ((m = segment.match(new RegExp(`^(?:loop|repeat) ${CLIP} (\\d+) times$`)))) {
    const loops = Number(m[1]);
    if (loops < 1 || loops > MAX_CLIP_LOOPS) return err(segment, `A clip can repeat 1 to ${MAX_CLIP_LOOPS} times.`, 'loop the selected clip 4 times');
    return { command: { type: 'clip.loop', loops } };
  }
  if (new RegExp(`^(?:don'?t loop|stop looping|unloop) ${CLIP}$`).test(segment)) return { command: { type: 'clip.loop', loops: 1 } };

  return null;
}

export const isAudioCommand = (command: Command) => command.type.startsWith('sample.') || command.type.startsWith('clip.');
